// @flow
import React, { useState, useEffect } from 'react'
import { styled } from 'styled-components'
import {
	CULMINATING_MOMENT_INPUT_DECISION_SRCS,
	CULMINATING_MOMENT_PRE_COUNT_UP_SRCS,
	CULMINATING_MOMENT_DECISIONS_INPUTTED_SRCS,
	CULMINATING_MOMENT_SCREEN_COUNTDOWN_COMPLETE_SRCS,
	CULMINATING_MOMENT_COUNT_UP_BEGIN_SRCS,
	CULMINATING_MOMENT_COUNT_UP_SRCS,
} from '@mission.io/mission-toolkit/constants'
import {
	useWatchTimers,
	useLocalTimers,
	useOnCulminatingMomentEnd,
	getCulminatingMomentVoteCount,
	useMissionStateSelector,
	getClassQuestion,
	getStudentClassQuestionResponses,
	getClassQuestionId,
	getCulminatingMomentResult,
	getScreenTimerForCustomScreenHandling,
	isPaused,
	getTimers,
	useIsSilent,
	useResolvers,
	useVolumeOf,
} from '../../../../context'
import {
	randomFromArray,
	ONE_SECOND,
	VOLUMES,
	cssPlayerHeightUnit,
} from '../../../../utility'

import {
	CulminatingMomentHeader,
	MarkdownHeader,
	PlayerWrapper,
} from '../sharedComponents'
import Option from './Option'
import { ConnectedAudio } from '../../../../sound/ConnectedMedia'
import { v4 as uuid } from 'uuid'
import { OptionalMarkdown } from '@mission.io/question-views'
import { useIsJuniorPlus } from '../../../../context/provider'

const COUNT_UP_SOUND_ID = uuid()

export default function CulminatingMomentScreen(): React$Node {
	const [showCulminatingMomentIntro, setShowCulminatingMomentIntro] =
		useState(true)
	const isSilent = useIsSilent()
	const { soundEffectSourceResolver } = useResolvers()
	const classQuestion = useMissionStateSelector(getClassQuestion)
	const classResponses = useMissionStateSelector(
		getStudentClassQuestionResponses
	)
	const onCulminatingMomentEnd = useOnCulminatingMomentEnd()
	const culminatingMomentResult = useMissionStateSelector(
		getCulminatingMomentResult
	)
	const culminatingMomentId = useMissionStateSelector(getClassQuestionId)
	const [currentVoteCount, totalPossibleVoteCount] = useMissionStateSelector(
		getCulminatingMomentVoteCount
	)
	const isJrPlus = useIsJuniorPlus()

	const missionIsPaused = useMissionStateSelector(isPaused)
	const screenTimerId = useMissionStateSelector(
		getScreenTimerForCustomScreenHandling
	)
	const deltaClockTimers = useMissionStateSelector(getTimers([screenTimerId]))
	const timer = useWatchTimers({
		deltaClockTimers,
		updateRate: missionIsPaused ? Infinity : ONE_SECOND / 2,
	})[screenTimerId]
	const localTimers = useLocalTimers(missionIsPaused)
	const vocalTrackVolume = useVolumeOf(VOLUMES.VOCAL_TRACK, false)
	const [audio, setAudio] = useState<
		{
			src: string,
			id: string,
			onEnd: () => void,
			applyMissionPause?: boolean,
		}[]
	>([])

	useEffect(() => {
		if (culminatingMomentResult) {
			// play before animation vocal track on one window
			setAudio(audio => [
				...audio,
				{
					src: randomFromArray(CULMINATING_MOMENT_PRE_COUNT_UP_SRCS),
					onEnd: () => {},
					id: uuid(),
				},
			])
		}

		return () => {
			//  clear all vocal tracks
			setAudio([])
		}
	}, [culminatingMomentResult, setAudio])

	useEffect(() => {
		// wait 4 seconds to show the culmMoment
		localTimers.addTimer(() => setShowCulminatingMomentIntro(false), 4000)
	}, [localTimers])

	// play a sound effect when the voting timer reaches 0
	const timeLeft = timer?.timeLeft
	const [timeEndedForScreenTimer, setTimeEndedForScreenTimer] = useState(null)
	const [timerStarted, setTimerStarted] = useState(false)
	useEffect(() => {
		if (
			// should only pass if the voting screen timer has just reached 0
			timeLeft == null &&
			timerStarted &&
			!culminatingMomentResult &&
			timeEndedForScreenTimer !== screenTimerId
		) {
			setTimeEndedForScreenTimer(screenTimerId)
			setTimerStarted(false)
			setAudio(audio => [
				...audio,
				{
					src: randomFromArray(
						CULMINATING_MOMENT_SCREEN_COUNTDOWN_COMPLETE_SRCS
					),
					id: uuid(),
					onEnd: () => {},
					applyMissionPause: false,
				},
			])
		}
		if (typeof timeLeft === 'number' && !culminatingMomentResult) {
			setTimeEndedForScreenTimer(null)
			setTimerStarted(true)
		}
	}, [
		timeLeft,
		screenTimerId,
		timeEndedForScreenTimer,
		culminatingMomentResult,
		timerStarted,
	])

	const questionImageUrl =
		classQuestion?.media?.[0]?.type === 'IMAGE' && classQuestion.media[0].url
	const options = classQuestion?.options || []

	return (
		<PlayerWrapperStyled>
			{showCulminatingMomentIntro || !classQuestion ? (
				<PlayerPhrase>Critical Decision</PlayerPhrase>
			) : (
				<>
					<CulminatingMomentHeader
						{...{
							timeLeft: timeLeft ?? 0,
							hideTimer: Boolean(culminatingMomentResult) && Boolean(timer),
							rightChild:
								(Boolean(culminatingMomentResult) && Boolean(timer)) ||
								timer?.timeLeft === 0
									? `${currentVoteCount} / ${totalPossibleVoteCount} votes`
									: null,
						}}>
						<MarkdownHeader className="font-game-body">
							<OptionalMarkdown
								interpretAsMarkdown={true}
								className="prose-on-game-light-purple !text-[calc(var(--ph)*4)]">
								{classQuestion.phrase}
							</OptionalMarkdown>
						</MarkdownHeader>
					</CulminatingMomentHeader>
					<ContentSection className="space-x-[calc(var(--ph)*2)]">
						{questionImageUrl && (
							<DivWithBackgroundImage $questionImageUrl={questionImageUrl} />
						)}
						<OptionsWrapper>
							{options.map((option, index) => {
								return (
									<Option
										key={`image_${option._id}`}
										option={option}
										result={culminatingMomentResult}
										rowCount={options.length}
										onCountUpStart={() => {
											if (index === 0) {
												setAudio(currentAudio => [
													...currentAudio,
													{
														src: randomFromArray(
															CULMINATING_MOMENT_COUNT_UP_BEGIN_SRCS
														),
														id: uuid(),
														onEnd: () => {},
													},
													{
														src: randomFromArray(
															CULMINATING_MOMENT_COUNT_UP_SRCS
														),
														id: COUNT_UP_SOUND_ID,
														onEnd: () => {},
													},
												])
											}
										}}
										onCountUpEnd={() => {
											if (index === 0) {
												setAudio(currentAudio => [
													...currentAudio.filter(
														({ id }) => id !== COUNT_UP_SOUND_ID
													),
													{
														src: randomFromArray(
															CULMINATING_MOMENT_INPUT_DECISION_SRCS
														),
														onEnd: () => {
															setAudio(currentAudio => [
																...currentAudio,
																{
																	src: randomFromArray(
																		isJrPlus
																			? CULMINATING_MOMENT_DECISIONS_INPUTTED_SRCS.jrPlus
																			: CULMINATING_MOMENT_DECISIONS_INPUTTED_SRCS.jr
																	),
																	id: uuid(),
																	onEnd: () => {
																		if (culminatingMomentId && !isSilent) {
																			onCulminatingMomentEnd(
																				culminatingMomentId
																			)
																		}
																	},
																},
															])
														},
														id: uuid(),
													},
												])
											}
										}}
										index={index}
										proportion={
											(classResponses[option._id]?.length || 0) /
											(currentVoteCount || 1)
										}
										isCompressed={!!questionImageUrl}
									/>
								)
							})}
						</OptionsWrapper>
					</ContentSection>
				</>
			)}
			{audio.map(audioData => (
				<ConnectedAudio
					key={audioData.id}
					id={audioData.id}
					autoPlay
					onEnded={() => {
						setAudio(oldAudio =>
							oldAudio.filter(data => data.id !== audioData.id)
						)
						if (audioData.onEnd) {
							audioData.onEnd()
						}
					}}
					applyMissionPause={
						audioData.applyMissionPause != null
							? audioData.applyMissionPause
							: true
					}
					src={soundEffectSourceResolver(audioData.src)}
					volume={vocalTrackVolume}
					muted={isSilent}
				/>
			))}
		</PlayerWrapperStyled>
	)
}

const PlayerWrapperStyled = styled(PlayerWrapper)`
	display: flex;
	flex-direction: column;
	height: 100%;
`
const PlayerPhrase = styled.div.attrs({ className: 'font-game-headline' })`
	font-size: 8rem;
	font-weight: ${({ theme }) => theme.fontSemiBold};
	display: flex;
	justify-content: center;
	align-items: center;
	height: 100%;
`

const ContentSection = styled.div`
	padding: ${({ theme }) => cssPlayerHeightUnit(4)};
	flex: 1;
	margin: 0 4% ${cssPlayerHeightUnit(10)} 4%;
	display: flex;

	align-items: flex-start;
`

const OptionsWrapper = styled.div`
	display: flex;
	flex-direction: column;
	height: 100%;
	flex: 1;
`

const DivWithBackgroundImage = styled.div`
	background: url('${({ $questionImageUrl }) => $questionImageUrl}');
	height: 100%;
	flex: 1;
	background-repeat: no-repeat;
	background-size: contain;
	background-position: top;
`
